import { apiClient as api } from './api';
import { env } from '../env.config';
import { logger } from '../utils/logger';

// Types pour la conformité médicale
export interface ComplianceConfig {
  medicalComplianceEnabled: boolean;
  emergencyContact: string;
  hdsCertified: boolean;
  gdprCompliant: boolean;
  encryptionEnabled: boolean;
  twoFactorEnabled: boolean;
  sessionTimeout: number;
}

export interface ConsentData {
  userId: string;
  consentType: 'medical' | 'data' | 'marketing';
  granted: boolean;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  timestamp: string;
}

// Configuration de conformité depuis l'environnement
const COMPLIANCE_CONFIG: ComplianceConfig = {
  medicalComplianceEnabled: env.MEDICAL_COMPLIANCE_ENABLED,
  emergencyContact: env.EMERGENCY_CONTACT,
  hdsCertified: env.HDS_CERTIFIED,
  gdprCompliant: env.GDPR_COMPLIANT,
  encryptionEnabled: env.ENCRYPTION_ENABLED,
  twoFactorEnabled: env.TWO_FACTOR_ENABLED,
  sessionTimeout: env.SESSION_TIMEOUT
};

// Service de conformité médicale
class ComplianceService {
  private config: ComplianceConfig;

  constructor() {
    this.config = COMPLIANCE_CONFIG;
  }

  // Obtenir la configuration de conformité
  getComplianceConfig(): ComplianceConfig {
    return this.config;
  }

  // Vérifier si la conformité médicale est activée
  isMedicalComplianceEnabled(): boolean {
    return this.config.medicalComplianceEnabled;
  }

  // Vérifier si l'application est certifiée HDS
  isHdsCertified(): boolean {
    return this.config.hdsCertified;
  }

  // Vérifier si l'application est conforme RGPD
  isGdprCompliant(): boolean {
    return this.config.gdprCompliant;
  }

  // Enregistrer le consentement utilisateur
  async saveConsent(consentData: Omit<ConsentData, 'timestamp'>): Promise<void> {
    try {
      const fullConsentData: ConsentData = {
        ...consentData,
        timestamp: new Date().toISOString()
      };

      await api.post('/api/compliance/consent', fullConsentData);
      
      // Sauvegarder localement pour référence
      localStorage.setItem('medical_consent', JSON.stringify(fullConsentData));
      
      // Consentement enregistré avec succès
    } catch (error) {
      logger.error('Erreur lors de l\'enregistrement du consentement:', error);
      throw new Error('Impossible d\'enregistrer le consentement');
    }
  }

  // Récupérer le consentement utilisateur
  async getConsent(userId: string): Promise<ConsentData | null> {
    try {
      const response = await api.get<ConsentData>(`/api/compliance/consent/${userId}`);
      return response;
    } catch (error) {
      logger.error('Erreur lors de la récupération du consentement:', error);
      return null;
    }
  }

  // Valider les données médicales
  async validateMedicalData(data: Record<string, unknown>, type: string): Promise<ValidationResult> {
    try {
      const response = await api.post<ValidationResult>('/api/medical/validate', {
        type,
        data
      });
      
      return response;
    } catch (error) {
      logger.error('Erreur lors de la validation des données médicales:', error);
      return {
        isValid: false,
        errors: ['Erreur de validation'],
        warnings: [],
        timestamp: new Date().toISOString()
      };
    }
  }

  // Enregistrer un événement d'audit
  async logAuditTrail(action: string, details: Record<string, unknown>): Promise<void> {
    try {
      await api.post('/api/audit/log', {
        action,
        details,
        timestamp: new Date().toISOString(),
        ipAddress: 'client-ip', // À récupérer côté client
        userAgent: navigator.userAgent
      });
    } catch (error) {
      logger.error('Erreur lors de l\'enregistrement de l\'audit:', error);
    }
  }

  // Vérifier la sécurité de la session
  checkSessionSecurity(): boolean {
    const token = localStorage.getItem(env.JWT_STORAGE_KEY);
    if (!token) return false;
    
    // Vérifier si la session n'a pas expiré
    const sessionStart = localStorage.getItem('session_start');
    if (sessionStart) {
      const sessionAge = Date.now() - parseInt(sessionStart);
      if (sessionAge > this.config.sessionTimeout * 1000) {
        return false;
      }
    }
    
    return true;
  }

  // Générer un rapport de conformité
  async generateComplianceReport(): Promise<Record<string, unknown> | null> {
    try {
      const response = await api.get<Record<string, unknown>>('/api/compliance/report');
      return response;
    } catch (error) {
      logger.error('Erreur lors de la génération du rapport:', error);
      return null;
    }
  }

  // Notifier une urgence
  async notifyEmergency(emergencyData: {
    type: string;
    description: string;
    location?: string;
    patientId?: string;
  }): Promise<void> {
    try {
      await api.post('/api/emergency/notify', {
        ...emergencyData,
        timestamp: new Date().toISOString(),
        ipAddress: 'client-ip',
        userAgent: navigator.userAgent
      });
      
      // Urgence notifiée avec succès
    } catch (error) {
      logger.error('Erreur lors de la notification d\'urgence:', error);
      throw new Error('Impossible de notifier l\'urgence');
    }
  }

  // Vérifier la conformité globale
  async checkGlobalCompliance(): Promise<{
    compliant: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    try {
      const response = await api.get<{ compliance?: Record<string, boolean> }>('/health');
      const compliance = response.compliance || {};
      
      const issues: string[] = [];
      const recommendations: string[] = [];
      
      if (!compliance.gdprCompliant) {
        issues.push('Non conforme RGPD');
        recommendations.push('Activer la conformité RGPD');
      }
      
      if (!compliance.hdsCertified) {
        issues.push('Non certifié HDS');
        recommendations.push('Obtenir la certification HDS');
      }
      
      if (!compliance.encryptionEnabled) {
        issues.push('Chiffrement non activé');
        recommendations.push('Activer le chiffrement des données');
      }
      
      return {
        compliant: issues.length === 0,
        issues,
        recommendations
      };
    } catch (error) {
      logger.error('Erreur lors de la vérification de conformité:', error);
      return {
        compliant: false,
        issues: ['Impossible de vérifier la conformité'],
        recommendations: ['Vérifier la connexion au serveur']
      };
    }
  }
}

export const complianceService = new ComplianceService();
export default complianceService;
